import { useState, useEffect, useCallback } from 'react';
import { CURRENT_INTEREST, MATURITY_YEARS } from '../../constants';
import { calculateMonthlyInstallment } from '../utils/calculateMonthlyInstallment';
import { calculateMonthlyInterest } from '../utils/calculateMonthlyInterest';
import { getMonthlyInterestRate } from '../utils/getMonthlyInterestRate';

interface CalculationResults {
  loanAmount: number | null;
  maturityYears: number | null;
  interestRate: number | null;
  monthlyInstallment: number | null;
  amortization: number | null;
  interestPayment: number | null;
}

const initialLoanData = {
  loanAmount: null,
  maturityYears: null,
  interestRate: null,
  monthlyInstallment: null,
  amortization: null,
  interestPayment: null,
};

export const useAffordabilityCalculator = (amount: number, isDisabled: boolean) => {
  const [calculationResults, setCalculationResults] = useState<CalculationResults>(initialLoanData);

  const calculateFinance = useCallback(
    (principal: number) => {
      const interestRate = getMonthlyInterestRate();
      const monthlyInstallment = calculateMonthlyInstallment(principal, interestRate);
      const interestPayment = calculateMonthlyInterest(amount);

      const amortization = monthlyInstallment - interestPayment;

      return {
        principal,
        monthlyInstallment,
        amortization,
        interestPayment,
        interestRate,
      };
    },
    [amount],
  );

  const resetCalculations = useCallback(() => {
    setCalculationResults(initialLoanData);
  }, []);

  useEffect(() => {
    if (isDisabled) {
      resetCalculations();
    } else {
      const { principal, monthlyInstallment, amortization, interestPayment } = calculateFinance(amount);

      setCalculationResults({
        loanAmount: principal,
        maturityYears: MATURITY_YEARS,
        interestRate: CURRENT_INTEREST,
        monthlyInstallment: Math.round(monthlyInstallment),
        amortization: Math.round(amortization),
        interestPayment: Math.round(interestPayment),
      });
    }
  }, [amount, calculateFinance, isDisabled, resetCalculations]);

  return {
    initialLoanData,
    calculationResults,
    calculateFinance,
    resetCalculations,
  };
};
